<template>
  <div class="order-form">
    <md-card>
      <md-card-header>
        <translate tag="div" class="md-title">Place New Order</translate>
        <translate tag="div" class="md-subhead">Select products and their quantity</translate>
      </md-card-header>

      <ol class="progress">
        <li :class="{'is-active': step === 1, 'is-complete': step > 1}" data-step="1"><translate>Select Products</translate></li>
        <li :class="{'is-active': step === 2, 'is-complete': step > 2}" data-step="2"><translate>Review Order</translate></li>
        <li :class="{'is-active': step === 3}" data-step="3"><translate translate-comment="Heading on the front-end when an order has been completed.">Completed</translate></li>
      </ol>

      <md-card-content v-if="step !== 3">

        <md-layout md-gutter="8">
          <md-layout md-align="end"><translate class="text">DEPOT:</translate></md-layout>
          <md-layout>
            <translate tag="div" v-if="customers.length === 0" class="text">Not assigned</translate>
            <div v-else-if="customers.length === 1 || step === 2" class="text">{{ customers[selected].name }}</div>
            <div v-else>
              <md-radio
                @change="customerChange"
                class="text"
                v-for="(customer, index) in customers"
                v-model="selected"
                :id="'radio-' + index"
                :name="'radio-' + index"
                :md-value="index"
                :key="customer._id"
              >{{customer.name}}</md-radio>
            </div>
          </md-layout>
        </md-layout>

        <md-layout md-gutter="8">
          <md-layout md-align="end"><translate class="text">ADDRESS:</translate></md-layout>
          <md-layout><span class="text text-address">{{ stringifyAddress }}</span></md-layout>
        </md-layout>

        <md-layout md-gutter="8">
          <md-layout md-align="end"><translate class="text">CLIENT:</translate></md-layout>
          <md-layout><span class="text">{{ order.name }}</span></md-layout>
        </md-layout>

        <md-layout md-gutter="8">
          <md-layout md-align="end"><translate class="text">DATE:</translate></md-layout>
          <md-layout><span class="text">{{ order.date }}</span></md-layout>
        </md-layout>

        <order-review v-if="step === 2" :order="order"></order-review>
        <product-selector v-else :products="order.products" :prodAvailable="availableProducts" :order="true" :errorMsg="errorMsg" @addItem="addItem" @removeItem="removeItem"></product-selector>

        <md-layout md-align="center">
          <translate class="text-total md-body-2">TOTAL: %{ totalPrice } € (incl. VAT)</translate>
          <span v-if="containsCustom" class="total-warning"><md-icon>info<md-tooltip md-direction="right"><translate>The price might differ due to the custom products</translate></md-tooltip></md-icon></span>
        </md-layout>

      </md-card-content>
      <md-card-content v-else>
        <div v-if="creatingOrder !== undefined">
          <div class="order-placed"><translate>Your order #</translate><strong>{{ ('000000' + creatingOrder.orderId).slice(-6) }} </strong><translate>has been placed!</translate></div>
          <translate tag="div" class="order-placed">Please print the order confirmation and put it in the bag together with your clothes.</translate>
          <a @click="pdfNotDownloaded()" href="#"><translate>Click here if the download didn't start automatically.</translate></a>

        </div>
      </md-card-content>

      <md-card-actions v-if="step === 1">
        <md-button @click.native="resetForm()"><translate>Reset Form</translate></md-button>
        <md-button class="md-raised md-primary" @click.native="reviewOrder()"><translate>Review Order</translate></md-button>
      </md-card-actions>
      <md-card-actions v-if="step === 2">
        <md-spinner v-if="spinning" :md-size="20" md-indeterminate></md-spinner>
        <md-button @click.native="goBack()">Back</md-button>
        <md-button class="md-raised md-primary" @click.native="placeOrder()"><translate>Place Order</translate></md-button>
      </md-card-actions>
      <md-card-actions v-if="step === 3">
        <md-button class="md-raised md-primary" @click.native="placeNew()"><translate>Place New Order</translate></md-button>
      </md-card-actions>

      <md-snackbar md-position="bottom center" ref="snackbar" :md-duration="5000">
        <span>{{ snackbarMsg }}</span>
        <md-button @click.native="$refs.snackbar.close()"><translate>Close</translate></md-button>
      </md-snackbar>

    </md-card>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import Vue from 'vue'
import OrderPdf from '../common/OrderPdf'
import ProductCard from '../admin/products/Card'
import ProductSelector from '../common/Selector'
import OrderReview from './Review'
import auth from '../../auth'
import moment from 'moment'
import 'moment/locale/fr'
import 'moment/locale/de'

export default {
  name: 'order-form',
  data () {
    return {
      step: 1,
      spinning: false,
      order: {},
      selected: 0,
      customers: [],
      productLists: [],
      snackbarMsg: this.$gettext('Please, insert some items into shopping cart first!'),
      errorMsg: ''
    }
  },
  props: [ 'tabActive' ],
  watch: {
    tabActive (tabIndex) {
      // if we are at the last step and the tab is changed to "Past Orders", the form is reset to step 1
      if (tabIndex === 1 && this.step === 3) {
        this.placeNew()
      }
    }
  },
  async created () {
    this.orderCreate()
    this.order = this.creatingOrder // get the default value from store/Orders.js
    this.order.email = auth.getEmail()
    this.order.name = `${auth.getFirstname()} ${auth.getLastname()}`
    this.setDate()
    await this.getCustomers()
    await this.getProductLists()
  },
  updated () {
    // date of placing order is updated every time the form is updated
    this.setDate()
  },
  computed: {
    totalPrice () {
      return this.order.products.reduce((a, b) => {
        return a + b.price * b.quantity
      }, 0).toFixed(2)
    },
    totalItems () {
      return this.order.products.reduce((a, b) => {
        return a + b.quantity
      }, 0)
    },
    containsCustom () {
      return this.order.products.some((prod) => {
        return prod.custom
      })
    },
    availableProducts () {
      return this.productLists[this.selected] || []
    },
    stringifyAddress () {
      let address = (this.customers.length !== 0) ? this.customers[this.selected].address : undefined
      if (address && address.street && address.postcode && address.city && address.country) {
        return address.street + '\n' + address.postcode + ' ' + address.city + '\n' + address.country
      } else {
        return this.$gettext('Not available')
      }
    },
    ...mapGetters([
      'allCustomers',
      'allProductLists',
      'allProducts',
      'creatingOrder',
      'orderError'
    ])
  },
  methods: {
    ...mapMutations([
      'orderCreate',
      'orderCancelError'
    ]),
    ...mapActions([
      'saveOrder'
    ]),
    async getCustomers () {
      try {
        let userId = auth.getUserId()
        await this.$store.dispatch('fetchCustomersByUser', this.order.email)
        // user is assigned to these depots
        this.customers = this.allCustomers.filter((cust) => {
          return cust.users.includes(userId)
        })

        if (this.customers.length !== 0) {
          this.customers = this.customers.map((cust) => {
            return {
              'customId': cust.customId,
              'address': cust.address,
              'name': cust.name,
              'productList': cust.productList
            }
          })
          this.selected = 0
          this.order.address = this.customers[this.selected].address
          this.order.customer = this.customers[this.selected].name
          this.order.customerId = this.customers[this.selected].customId
        } else {
          this.errorMsg = this.$gettext('No depot assigned')
        }
      } catch (err) {
        this.snackbarMsg = this.$gettext('An error occurred while loading data. Try to reload the page and if the problem persists, please, contact the administrator.')
        this.$refs.snackbar.open()
      }
    },
    async getProductLists () {
      console.log('getProductLists')
      let currentCust = {}
      await this.$store.dispatch('fetchProducts')

      for (let i = 0; i < this.customers.length; i++) {
        currentCust = this.customers[i]

        if (currentCust.productList !== '') {
          await this.$store.dispatch('fetchOneProductList', currentCust.productList)
          let currentProdList = this.allProductLists.find((pList) => {
            return pList._id === currentCust.productList
          })

          if (currentProdList && currentProdList.products && currentProdList.products.length !== 0) {
            let products = this.getProducts(currentProdList)
            // add one more "custom" product at the end of list of products
            products.push({
              custom: true,
              name: '',
              price: 0,
              description: ''
            })
            this.productLists.push(products)
          } else {
            this.productLists.push([])
          }
        } else {
          this.errorMsg = this.$gettext('No product list assigned to depot')
        }
      }
    },
    getProducts (productList) {
      return productList.products.map((product) => {
        let productOrig = this.allProducts.find((prod) => {
          return prod._id === product.id
        })
        return {
          _id: product.id,
          custom: false,
          name: productOrig.name,
          price: product.price,
          description: productOrig.description
        }
      })
    },
    customerChange (val) {
      this.selected = val
      this.order.products = [] // shopping cart is emptied when user switch depot
      this.order.address = this.customers[this.selected].address
      this.order.customer = this.customers[this.selected].name
      this.order.customerId = this.customers[this.selected].customId
    },
    setDate () {
      let date = new Date()
      this.order.dateRaw = date.getTime()
      this.order.date = moment(date).locale(Vue.config.language).format('L LT')
    },
    resetForm () {
      for (let i = this.order.products.length - 1; i >= 0; i--) {
        this.removeItem(this.order.products[i])
      }
    },
    reviewOrder () {
      if (this.order.products.length === 0) {
        this.snackbarMsg = this.$gettext('Please, insert some items into shopping cart first!')
        this.$refs.snackbar.open()
      } else {
        this.step = 2
      }
    },
    goBack () {
      this.step = 1
    },
    placeOrder () {
      console.log('Order placed')
      this.order.totalPrice = this.totalPrice
      this.order.totalItems = this.totalItems

      this.spinning = true
      this.saveOrder()
        .then(() => {
          if (this.orderError) {
            this.snackbarMsg = this.$gettext('An error occurred while placing order. Try to reload the page and place order again. If the problem persists, please, contact the administrator.')
            this.$refs.snackbar.open()
            this.orderCancelError()
          } else {
            this.step = 3
            OrderPdf.downloadPdf(this.creatingOrder, this)
            console.log('PDF generated')
          }
        })
        .catch((err) => {
          console.log(err)
        })
      this.spinning = false
    },
    pdfNotDownloaded () {
      OrderPdf.openPdf(this.creatingOrder, this)
    },
    placeNew () {
      this.step = 1
      this.resetForm()
      this.orderCreate()
      this.order = this.creatingOrder // get the default value from store/Orders.js

      this.order.email = auth.getEmail()
      this.order.name = `${auth.getFirstname()} ${auth.getLastname()}`
      this.order.address = this.customers[this.selected].address
      this.order.customer = this.customers[this.selected].name
      this.order.customerId = this.customers[this.selected].customId
      this.setDate()
    },
    addItem (item) {
      this.order.products.push(item)
    },
    removeItem (item) {
      this.order.products = this.order.products.filter((prod) => {
        return prod.id !== item.id
      })
    }
  },
  components: {
    ProductCard,
    ProductSelector,
    OrderReview
  }
}
</script>

<style scoped>
.text {
  padding: 5px;
  font-size: 1.2em;
  margin-top: 0px;
  margin-bottom: 0px;
  line-height: 20px;
}
.text-address {
  white-space: pre-wrap;
  text-align: left;
}
.text-total {
  padding: 10px;
  font-size: 1.3em;
}
.total-warning {
  padding-top: 10px;
  padding-bottom: 10px;
  color: rgba(10, 81, 181, .35);
}
.order-placed {
  font-size: 1.3em;
  margin: 25px;
}
.order-form div.md-card {
  box-shadow: 0 1px 1px rgba(0,0,0,.08), 0 2px 2px rgba(0,0,0,.08);
}

/* Style for checkout progress bar */
/* Source: https://fribly.com/2015/01/01/scalable-responsive-progress-bar/ */

.progress {
  list-style: none;
  margin-top: 10px;
  margin-bottom: 1em;
  padding: 0;
  display: table;
  table-layout: fixed;
  width: 100%;
  font-size: 1.4em;
  color: #A2AAB5;
}
.progress > li {
  position: relative;
  display: table-cell;
  text-align: center;
  font-size: 0.8em;
}
.progress > li:before {
  content: attr(data-step);
  display: block;
  margin: 0 auto;
  background: #DAE5F4;
  width: 3em;
  height: 3em;
  text-align: center;
  margin-bottom: 0.25em;
  line-height: 3em;
  border-radius: 100%;
  position: relative;
  z-index: 1000;
}
.progress > li:after {
  content: '';
  position: absolute;
  display: block;
  background: #DAE5F4;
  width: 100%;
  height: 0.5em;
  top: 1.25em;
  left: 50%;
  margin-left: 1.5em\9;
  z-index: -1;
}
.progress > li:last-child:after {
  display: none;
}
.progress > li.is-complete {
  color: #3f51b5;
}
.progress > li.is-complete:before,
.progress > li.is-complete:after {
  color: #FFF;
  background: #3f51b5;
}
.progress > li.is-active {
  color: #3f51b5;
}
.progress > li.is-active:before {
    color: #FFF;
    background: #3f51b5;
}
</style>



// WEBPACK FOOTER //
// src/components/order/Form.vue